import { useEffect, useMemo, useRef, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { MODEL_IMAGES } from "components/PreviewOverlay/types"
import {
  ALL_GLASSES_DRAFTS_QUERY_KEY,
  PRODUCTS_QUERY_KEY,
  type ProductsQueryResponse,
  useProductClientClearCache,
} from "hooks/query/useProductsQuery"
import { useAppSelector } from "hooks/storeHooks"
import { useAlert } from "providers/AlertProvider"
import { type RuntimeGlassesControls } from "services/player/glasses-effect"
import { capturePlatformEvent } from "services/platformAnalytics"
import { tintVTOService } from "services/tintvto"
import { selectUserInfo } from "store/user/userSlice"
import type { StripeErrorResponse } from "store/billing/types"
import {
  type GlassesDraftListItem,
  type GlassesEditorDto,
  type GlassesDigitizationSummary,
  type GlassesEditorInitialSku,
  type GlassesLimits,
  type GlassesEditorPrefill,
  type GlassesRenderingOptions,
  type GlassesSourceImageDto,
  IntegrationType,
  initRenderingOptions,
  type ProductV2,
} from "types"
import { parseQuery } from "utils/parseQuery"
import { createDigitizedGlassesZipEffect } from "utils/zipEffectFileReader"
import { useTintVTOAxiosService } from "services/axiosService"
import { getGenericUserErrorMessage } from "@/utils/userFacingErrors"

type LocalSourceImage = {
  file: File
  id: string
  previewUrl: string
  type: "local"
}

type RemoteSourceImage = {
  id: string
  previewUrl: string
  sourceImageKey: string
  type: "remote"
}

type EditorSourceImage = LocalSourceImage | RemoteSourceImage

const isLocalSourceImage = (image: EditorSourceImage): image is LocalSourceImage =>
  image.type === "local"

const isRemoteSourceImage = (image: EditorSourceImage): image is RemoteSourceImage =>
  image.type === "remote"

const toRemoteSourceImages = (
  sourceImages: GlassesSourceImageDto[] | undefined
): EditorSourceImage[] => {
  const seen = new Set<string>()

  return (sourceImages ?? []).flatMap((image): EditorSourceImage[] => {
    const sourceImageKey = image.sourceImageKey?.trim()
    const previewUrl = image.previewUrl?.trim()
    if (!sourceImageKey || !previewUrl || seen.has(sourceImageKey)) return []
    seen.add(sourceImageKey)

    return [
      {
        id: `remote-${sourceImageKey}`,
        previewUrl,
        sourceImageKey,
        type: "remote" as const,
      },
    ]
  })
}

type EditorState = {
  coverFile: File | null
  coverPreviewUrl: string
  createdFromImport: boolean
  draftId?: string
  digitization: GlassesDigitizationSummary
  effectUrl?: string | null
  externalId: string
  initialSkus?: GlassesEditorInitialSku[]
  merchantId: string
  name: string
  productId?: string
  sourceImages: EditorSourceImage[]
  url: string
  visible: boolean
}

const createDraftDigitization = (): GlassesDigitizationSummary => ({
  lastError: null,
  status: "draft",
})

// The user-savable fields covered by the Save changes / Discard / Back contract:
// exactly what saveChanges persists. Digitization state, source images and the
// preview model selection are intentionally excluded.
type SavedEditorFields = {
  coverPreviewUrl: string
  externalId: string
  name: string
  url: string
  visible: boolean
}

const toSavedEditorFields = (data: GlassesEditorDto): SavedEditorFields => ({
  coverPreviewUrl: data.cover,
  externalId: data.externalId?.trim() || "",
  name: data.name,
  url: data.url?.trim() || "",
  visible: data.visible,
})

const toEditorFields = (state: EditorState): SavedEditorFields => ({
  coverPreviewUrl: state.coverPreviewUrl,
  externalId: state.externalId,
  name: state.name,
  url: state.url,
  visible: state.visible,
})

const createInitialState = (
  merchantId: string,
  prefill?: GlassesEditorPrefill | null
): EditorState => ({
  coverFile: null,
  coverPreviewUrl: prefill?.cover || "",
  createdFromImport: Boolean(prefill?.initialSkus?.length),
  draftId: undefined,
  digitization: createDraftDigitization(),
  effectUrl: null,
  externalId: prefill?.externalId?.trim() || "",
  initialSkus: prefill?.initialSkus,
  merchantId: prefill?.merchantId?.trim() || merchantId,
  name: prefill?.name?.trim() || "",
  productId: undefined,
  sourceImages: [],
  url: prefill?.url?.trim() || "",
  visible: false,
})

const toLocalImage = (file: File): LocalSourceImage => ({
  file,
  id: `${file.name}-${file.lastModified}-${Math.random().toString(36).slice(2)}`,
  previewUrl: URL.createObjectURL(file),
  type: "local",
})

const buildPreviewProduct = (state: EditorState, effectState: RuntimeEffectState): ProductV2 => ({
  brand: "",
  brandSlug: "",
  categoryId: 3,
  colors: [],
  cover: state.coverPreviewUrl,
  coverFront: null,
  coverLeft: null,
  coverRight: null,
  coverage: "",
  effectUrl: state.effectUrl || undefined,
  externalId: state.externalId,
  finish: "",
  id: state.productId || "",
  merchant: state.merchantId,
  name: state.name,
  region: "",
  renderingOptions: effectState.renderingOptions,
  sku: state.externalId,
  type: "GLASSES",
  upc: "",
  url: state.url,
  visible: state.visible,
})

type HydrationStatus = "idle" | "loading" | "ready" | "fallback"

type RuntimeEffectState = {
  hydrationStatus: HydrationStatus
  renderingOptions: GlassesRenderingOptions
}

const cloneRenderingOptions = (
  value: GlassesRenderingOptions = initRenderingOptions
): GlassesRenderingOptions => ({
  ...(value.bodyGloss !== undefined ? { bodyGloss: value.bodyGloss } : {}),
  ...(value.lensesGloss !== undefined ? { lensesGloss: value.lensesGloss } : {}),
  ...(value.transparency !== undefined ? { transparency: value.transparency } : {}),
  positionAccuracy: { ...value.positionAccuracy },
  scale: { ...value.scale },
})

const applyRuntimeControls = (
  current: GlassesRenderingOptions,
  runtimeControls: RuntimeGlassesControls
): GlassesRenderingOptions => ({
  ...current,
  positionAccuracy: {
    ...current.positionAccuracy,
    x: runtimeControls.positionX,
    y: runtimeControls.positionY,
    z: runtimeControls.positionZ,
  },
  scale: {
    ...current.scale,
    x: runtimeControls.scaleX,
    y: runtimeControls.scaleX,
    z: runtimeControls.scaleX,
  },
})

const createInitialRuntimeEffectState = (
  hydrationStatus: HydrationStatus = "idle"
): RuntimeEffectState => ({
  hydrationStatus,
  renderingOptions: cloneRenderingOptions(),
})

const createFallbackName = (value: string): string => {
  const normalizedValue = value.trim()
  return normalizedValue || "My glasses"
}

const uploadSourceImage = async (options: {
  contentType: string
  file: File
  method: string
  uploadUrl: string
}) => {
  if (import.meta.env.DEV)
    return await fetch(`/__glasses-upload?url=${encodeURIComponent(options.uploadUrl)}`, {
      body: options.file,
      headers: {
        "Content-Type": options.contentType,
      },
      method: options.method || "PUT",
    })

  return await fetch(options.uploadUrl, {
    body: options.file,
    headers: {
      "Content-Type": options.contentType,
    },
    method: options.method || "PUT",
  })
}

const getErrorMessage = (_error: unknown, fallbackMessage: string): string => fallbackMessage

const getErrorDetails = (
  error: unknown
): {
  draftId?: string
  limits?: GlassesLimits
  productId?: string
  stage?: string
} | null => {
  if (!error || typeof error !== "object" || !("data" in error)) return null

  const data = (error as { data?: StripeErrorResponse | null }).data
  if (!data || typeof data !== "object" || !("details" in data)) return null

  const details = data.details
  if (!details || typeof details !== "object") return null

  const limits =
    "activeDigitizations" in details &&
    typeof details.activeDigitizations === "number" &&
    "maxActiveDigitizations" in details &&
    typeof details.maxActiveDigitizations === "number" &&
    "maxDrafts" in details &&
    typeof details.maxDrafts === "number" &&
    "totalDrafts" in details &&
    typeof details.totalDrafts === "number"
      ? {
          activeDigitizations: details.activeDigitizations,
          maxActiveDigitizations: details.maxActiveDigitizations,
          maxDrafts: details.maxDrafts,
          totalDrafts: details.totalDrafts,
        }
      : undefined

  return {
    draftId:
      "draftId" in details && typeof details.draftId === "string" ? details.draftId : undefined,
    limits,
    productId:
      "productId" in details && typeof details.productId === "string"
        ? details.productId
        : undefined,
    stage: "stage" in details && typeof details.stage === "string" ? details.stage : undefined,
  }
}

const getErrorCode = (error: unknown): string | null => {
  if (!error || typeof error !== "object" || !("data" in error)) return null

  const data = (error as { data?: StripeErrorResponse | null }).data
  if (!data || typeof data.code !== "string") return null

  return data.code
}

const createDraftLimitMessage = (limits: GlassesLimits): string =>
  `You already have ${limits.totalDrafts}/${limits.maxDrafts} drafts. Finish or cancel previous drafts before starting another try-on.`

const createDigitizationLimitMessage = (limits: GlassesLimits): string =>
  `You already have ${limits.activeDigitizations}/${limits.maxActiveDigitizations} try-ons in progress. Wait for one to finish or cancel one first.`

const resolveNextSourceImages = (
  current: EditorState,
  data: GlassesEditorDto,
  resetSourceImages?: boolean
): EditorSourceImage[] => {
  const remoteSourceImages = toRemoteSourceImages(data.sourceImages)

  // Right after digitization starts the backend can't serve source-image
  // previews yet (they only become available from the digitizer job result),
  // so an empty server list must not wipe the previews the user just uploaded.
  if (resetSourceImages)
    return remoteSourceImages.length > 0 ? remoteSourceImages : current.sourceImages

  return current.sourceImages.length === 0 && remoteSourceImages.length > 0
    ? remoteSourceImages
    : current.sourceImages
}

const toEditorStateFromDto = (
  current: EditorState,
  data: GlassesEditorDto,
  options?: {
    resetSourceImages?: boolean
    preserveLocalEdits?: boolean
  }
): EditorState => ({
  ...current,
  coverFile: options?.preserveLocalEdits ? current.coverFile : null,
  coverPreviewUrl:
    options?.preserveLocalEdits && current.coverFile ? current.coverPreviewUrl : data.cover,
  createdFromImport: data.createdFromImport,
  draftId: data.draftId ?? undefined,
  digitization: data.digitization,
  effectUrl: data.effectUrl ?? null,
  externalId: options?.preserveLocalEdits ? current.externalId : data.externalId?.trim() || "",
  initialSkus: current.initialSkus,
  merchantId: data.merchantId,
  name: options?.preserveLocalEdits ? current.name : data.name,
  productId: data.productId ?? undefined,
  sourceImages: resolveNextSourceImages(current, data, options?.resetSourceImages),
  url: options?.preserveLocalEdits ? current.url : data.url?.trim() || "",
  visible: options?.preserveLocalEdits ? current.visible : data.visible,
})

const hasRequiredFields = (state: EditorState): boolean =>
  Boolean(
    state.name.trim() && (state.coverPreviewUrl.trim() || state.coverFile || state.sourceImages[0])
  )

const canPublishProduct = (state: EditorState): boolean =>
  Boolean(
    state.name.trim() &&
      state.externalId.trim() &&
      state.coverPreviewUrl.trim() &&
      state.effectUrl?.trim() &&
      state.digitization.status === "ready"
  )

const removeDraftFromCachedCollections = (
  queryClient: ReturnType<typeof useQueryClient>,
  draftId: string
) => {
  queryClient.setQueriesData<GlassesDraftListItem[]>(
    { queryKey: ALL_GLASSES_DRAFTS_QUERY_KEY },
    (old) => old?.filter((draft) => draft.draftId !== draftId) ?? old
  )
}

const removeProductFromCachedCollections = (
  queryClient: ReturnType<typeof useQueryClient>,
  productId: string
) => {
  queryClient.setQueriesData<ProductsQueryResponse>({ queryKey: PRODUCTS_QUERY_KEY() }, (old) => {
    if (!old) return old

    return {
      ...old,
      items: old.items.filter((product) => product.id !== productId),
    }
  })
}

export const useGlassesEditor = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { showAlert } = useAlert()
  const queryClient = useQueryClient()
  const clearProductCache = useProductClientClearCache()
  const userInfo = useAppSelector(selectUserInfo)
  const axiosService = useTintVTOAxiosService()
  const genericErrorMessage = getGenericUserErrorMessage(t)

  const routePrefill =
    (
      location.state as {
        hydratedEditor?: GlassesEditorDto
        prefill?: GlassesEditorPrefill
      } | null
    )?.prefill ?? null
  const routeHydratedEditor =
    (
      location.state as {
        hydratedEditor?: GlassesEditorDto
        prefill?: GlassesEditorPrefill
      } | null
    )?.hydratedEditor ?? null
  const routeProductId = parseQuery(location.search).id
  const routeDraftId = parseQuery(location.search).draftId
  const consumedHydrationKeyRef = useRef<string | null>(null)
  const persistedVisibleRef = useRef(false)
  const currentEffectUrlRef = useRef<string | null>(null)
  const allowStartDigitizationSideEffectsRef = useRef(true)

  const [editor, setEditor] = useState<EditorState>(() =>
    createInitialState(userInfo.merchantId ?? "", routePrefill)
  )
  const [runtimeEffect, setRuntimeEffect] = useState<RuntimeEffectState>(
    createInitialRuntimeEffectState
  )
  const [isLoading, setIsLoading] = useState(Boolean(routeProductId || routeDraftId))
  const [isSaving, setIsSaving] = useState(false)
  const [isStarting, setIsStarting] = useState(false)
  const [isCancellingDraft, setIsCancellingDraft] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [limits, setLimits] = useState<GlassesLimits | null>(null)
  const [limitsUnavailable, setLimitsUnavailable] = useState(false)
  const [previewImage, setPreviewImage] = useState(MODEL_IMAGES[0])
  const [renderingDirty, setRenderingDirty] = useState(false)
  const [startFeedbackMessage, setStartFeedbackMessage] = useState<string | null>(null)
  // Last persisted values of the user-savable fields; null until an entity is loaded.
  const [savedEditorFields, setSavedEditorFields] = useState<SavedEditorFields | null>(null)
  // Bumped to remount the preview viewer so it re-applies the saved effect transforms.
  const [previewResetToken, setPreviewResetToken] = useState(0)
  const editorRef = useRef(editor)

  useEffect(() => {
    editorRef.current = editor
  })

  useEffect(() => {
    allowStartDigitizationSideEffectsRef.current = true
    return () => {
      allowStartDigitizationSideEffectsRef.current = false
    }
  }, [])

  const applyLoadedEditor = async (
    data: GlassesEditorDto,
    options?: { resetSourceImages?: boolean; preserveLocalEdits?: boolean }
  ) => {
    persistedVisibleRef.current = data.visible
    const nextEffectUrl = data.effectUrl ?? null
    const effectUrlChanged = currentEffectUrlRef.current !== nextEffectUrl
    currentEffectUrlRef.current = nextEffectUrl
    setEditor((current) => toEditorStateFromDto(current, data, options))
    if (options?.preserveLocalEdits) {
      // Background refreshes adopt the server cover unless a local file is staged
      // (mirrors toEditorStateFromDto) — keep the baseline in sync so server-driven
      // cover changes don't read as unsaved user edits.
      if (!editorRef.current.coverFile)
        setSavedEditorFields((prev) => (prev ? { ...prev, coverPreviewUrl: data.cover } : prev))
    } else {
      setSavedEditorFields(toSavedEditorFields(data))
    }
    if (!options?.preserveLocalEdits || effectUrlChanged) {
      setRuntimeEffect(createInitialRuntimeEffectState(data.effectUrl ? "loading" : "idle"))
      setRenderingDirty(false)
      setStartFeedbackMessage(null)
    }
  }

  const loadLimits = async (merchantIdOverride?: string): Promise<GlassesLimits | null> => {
    if (limitsUnavailable) return null

    const merchantId = merchantIdOverride || editor.merchantId || userInfo.merchantId
    if (!merchantId) {
      setLimits(null)
      return null
    }

    try {
      const nextLimits = await tintVTOService.getGlassesLimits({ merchantId })
      setLimits(nextLimits)
      return nextLimits
    } catch (error) {
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error as { status?: unknown }).status === 404
      )
        setLimitsUnavailable(true)

      setLimits(null)
      return null
    }
  }

  useEffect(() => {
    if (!routeProductId && !routeDraftId) {
      persistedVisibleRef.current = false
      setEditor(createInitialState(userInfo.merchantId ?? "", routePrefill))
      setRuntimeEffect(createInitialRuntimeEffectState())
      setRenderingDirty(false)
      setSavedEditorFields(null)
      setIsLoading(false)
      void loadLimits(routePrefill?.merchantId?.trim() || userInfo.merchantId || undefined)
      return
    }

    let cancelled = false

    const load = async () => {
      const canHydrateFromRouteState =
        routeHydratedEditor &&
        consumedHydrationKeyRef.current !== location.key &&
        ((routeProductId && routeHydratedEditor.productId === routeProductId) ||
          (routeDraftId && routeHydratedEditor.draftId === routeDraftId))

      if (!canHydrateFromRouteState) setIsLoading(true)

      try {
        if (canHydrateFromRouteState) {
          consumedHydrationKeyRef.current = location.key
          await applyLoadedEditor(routeHydratedEditor, { resetSourceImages: true })
          await loadLimits(routeHydratedEditor.merchantId)
          return
        }

        const data = routeProductId
          ? await tintVTOService.getGlassesEditor(routeProductId)
          : await tintVTOService.getGlassesDraft(routeDraftId)
        if (cancelled) return
        await applyLoadedEditor(data, { resetSourceImages: true })
        if (cancelled) return
        await loadLimits(data.merchantId)
      } catch (error) {
        if (!cancelled) {
          if (getErrorCode(error) === "glasses_draft_cancelled") {
            navigate("/glasses", { replace: true })
            showAlert({
              error: false,
              text: "Draft was cancelled",
            })
            return
          }
          showAlert({
            error: true,
            text: "Couldn't open this item",
          })
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [
    location.key,
    routeDraftId,
    routeHydratedEditor,
    routePrefill,
    routeProductId,
    showAlert,
    userInfo.merchantId,
  ])

  const previewProduct = useMemo(
    () => buildPreviewProduct(editor, runtimeEffect),
    [editor, runtimeEffect]
  )
  const isDraftEntity = Boolean(editor.draftId) && !editor.productId
  const isReady = editor.digitization.status === "ready" && Boolean(editor.effectUrl)
  const isEditableTryOnReady = isReady
  const isParsingTryOn = isReady && runtimeEffect.hydrationStatus === "loading"
  const isDraftReadyToFinalize = Boolean(editor.draftId) && isReady
  const draftLimitReached = Boolean(
    limits && !editor.draftId && !editor.productId && limits.totalDrafts >= limits.maxDrafts
  )
  const activeDigitizationLimitReached = Boolean(
    limits && limits.activeDigitizations >= limits.maxActiveDigitizations
  )
  const startRestrictionMessage = draftLimitReached
    ? createDraftLimitMessage(limits!)
    : activeDigitizationLimitReached
    ? createDigitizationLimitMessage(limits!)
    : null
  const canStartDigitization =
    !isStarting &&
    editor.sourceImages.length >= 1 &&
    editor.sourceImages.length <= 3 &&
    !startRestrictionMessage &&
    editor.digitization.status !== "queued" &&
    editor.digitization.status !== "processing"

  const uploadCoverIfNeeded = async (): Promise<string> => {
    if (!editor.coverFile && editor.coverPreviewUrl.trim().length > 0) return editor.coverPreviewUrl

    const firstLocalSourceImage = editor.sourceImages.find(isLocalSourceImage)
    const file = editor.coverFile ?? firstLocalSourceImage?.file
    if (!file) throw new Error("Couldn't upload photo")

    const {
      data: { authorizeCDN },
    } = await tintVTOService.authorizeCDN()

    return await tintVTOService.uploadCDN({
      body: file,
      jwt: authorizeCDN,
      name: tintVTOService.cdnImgUrlName({
        file,
        merchant: editor.merchantId,
      }),
      type: file.type,
    })
  }

  const resolveSourceImageKeys = async (): Promise<string[]> => {
    const keys: string[] = []

    for (const image of editor.sourceImages) {
      if (isRemoteSourceImage(image)) {
        keys.push(image.sourceImageKey)
        continue
      }

      const uploadUrl = await tintVTOService.createGlassesSourceImageUploadUrl({
        contentType: image.file.type || "image/png",
      })
      const response = await uploadSourceImage({
        contentType: image.file.type || uploadUrl.contentType,
        file: image.file,
        method: uploadUrl.method || "PUT",
        uploadUrl: uploadUrl.uploadUrl,
      })
      if (!response.ok) throw new Error("Couldn't upload photo")
      keys.push(uploadUrl.key)
    }

    return keys
  }

  const reloadCurrentEntity = async (override?: {
    draftId?: string
    productId?: string
    preserveLocalEdits?: boolean
  }) => {
    const productId = override?.productId || editor.productId
    if (productId) {
      const data = await tintVTOService.getGlassesEditor(productId)
      await applyLoadedEditor(data, { preserveLocalEdits: override?.preserveLocalEdits })
      await loadLimits(data.merchantId)
      return
    }

    const draftId = override?.draftId || editor.draftId
    if (draftId) {
      const data = await tintVTOService.getGlassesDraft(draftId)
      await applyLoadedEditor(data, { preserveLocalEdits: override?.preserveLocalEdits })
      await loadLimits(data.merchantId)
    }
  }

  const syncTiendanubeStoreVisibility = async (input: {
    merchantId: string
    nextVisible: boolean
    previousVisible: boolean
    productId: string
  }) => {
    if (userInfo.integrationType !== IntegrationType.TIENDANUBE) return
    if (input.nextVisible === input.previousVisible) return

    const product = await tintVTOService.productByIdV2(input.productId)
    const skus = product.colors.map((color) => color.sku).filter(Boolean)
    if (skus.length === 0) return

    if (input.nextVisible) await axiosService.updateMerchantSkus(input.merchantId, skus, [])
    else await axiosService.updateMerchantSkus(input.merchantId, [], skus)
  }

  useEffect(() => {
    if (editor.digitization.status !== "queued" && editor.digitization.status !== "processing")
      return
    if (!editor.productId && !editor.draftId) return

    const intervalId = window.setInterval(() => {
      void reloadCurrentEntity({ preserveLocalEdits: true }).catch(() => {
        // Ignore polling failures. The next refresh will retry.
      })
    }, 5000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [editor.digitization.status, editor.draftId, editor.productId])

  useEffect(() => {
    const handleFocus = () => {
      void loadLimits().catch(() => {
        // Ignore limits refresh failures. Backend checks still apply on submit.
      })
    }

    window.addEventListener("focus", handleFocus)
    return () => {
      window.removeEventListener("focus", handleFocus)
    }
  }, [editor.merchantId, userInfo.merchantId])

  useEffect(() => {
    if (editor.digitization.status !== "queued" && editor.digitization.status !== "processing")
      return
    if (!editor.productId && !editor.draftId) return

    const handleFocus = () => {
      void reloadCurrentEntity({ preserveLocalEdits: true }).catch(() => {
        // Ignore focus refresh failures. Polling will retry.
      })
    }

    window.addEventListener("focus", handleFocus)
    return () => {
      window.removeEventListener("focus", handleFocus)
    }
  }, [editor.digitization.status, editor.draftId, editor.productId])

  const startDigitization = async () => {
    if (!canStartDigitization) return

    setIsStarting(true)
    setStartFeedbackMessage(null)
    try {
      const nextLimits = await loadLimits()
      if (nextLimits) {
        if (
          !editor.draftId &&
          !editor.productId &&
          nextLimits.totalDrafts >= nextLimits.maxDrafts
        ) {
          if (!allowStartDigitizationSideEffectsRef.current) return
          showAlert({
            error: true,
            text: createDraftLimitMessage(nextLimits),
          })
          return
        }

        if (nextLimits.activeDigitizations >= nextLimits.maxActiveDigitizations) {
          if (!allowStartDigitizationSideEffectsRef.current) return
          showAlert({
            error: true,
            text: createDigitizationLimitMessage(nextLimits),
          })
          return
        }
      }

      const nextName = createFallbackName(editor.name)
      const externalId = editor.externalId.trim() || null
      const hasExplicitCover = Boolean(editor.coverFile || editor.coverPreviewUrl.trim())
      const [cover, sourceImageKeys] = await Promise.all([
        uploadCoverIfNeeded(),
        resolveSourceImageKeys(),
      ])
      if (editor.productId) {
        const response = await tintVTOService.startGlassesDigitization({
          cover,
          externalId,
          name: nextName,
          productId: editor.productId,
          sourceImageKeys,
          url: editor.url || null,
        })

        clearProductCache()
        if (!allowStartDigitizationSideEffectsRef.current) return
        setEditor((current) => ({
          ...current,
          coverFile: null,
          ...(hasExplicitCover ? { coverPreviewUrl: cover } : {}),
          digitization: response.digitization,
          externalId: response.externalId,
          name: nextName,
          productId: response.productId,
        }))
        // startGlassesDigitization persists cover/externalId/name/url server-side,
        // so the unsaved-changes baseline must follow (visible is not persisted here).
        setSavedEditorFields((prev) =>
          prev
            ? {
                ...prev,
                ...(hasExplicitCover ? { coverPreviewUrl: cover } : {}),
                externalId: response.externalId,
                name: nextName,
                url: editor.url,
              }
            : prev
        )
        void loadLimits(editor.merchantId)
      } else {
        const draft = editor.draftId
          ? await tintVTOService.startGlassesDraftDigitization(editor.draftId, {
              cover,
              externalId,
              ...(editor.initialSkus ? { initialSkus: editor.initialSkus } : {}),
              name: nextName,
              sourceImageKeys,
              url: editor.url || null,
            })
          : await tintVTOService.startInitialGlassesDraftDigitization({
              cover,
              externalId,
              ...(editor.initialSkus ? { initialSkus: editor.initialSkus } : {}),
              name: nextName,
              sourceImageKeys,
              url: editor.url || null,
            })

        clearProductCache()
        if (!allowStartDigitizationSideEffectsRef.current) return
        void loadLimits(editor.merchantId)
        await applyLoadedEditor(draft)
        if (!hasExplicitCover) setEditor((current) => ({ ...current, coverPreviewUrl: "" }))
        if (draft.draftId && !editor.draftId)
          navigate(`/glasses/add?draftId=${draft.draftId}`, {
            replace: true,
            state: { hydratedEditor: draft },
          })
      }
    } catch (error) {
      const message = getErrorMessage(error, genericErrorMessage)
      const details = getErrorDetails(error)
      const errorCode = getErrorCode(error)

      if (!allowStartDigitizationSideEffectsRef.current) return

      if (
        errorCode === "glasses_draft_limit_reached" ||
        errorCode === "glasses_digitization_limit_reached"
      ) {
        const nextLimits = details?.limits ?? (await loadLimits())
        if (!allowStartDigitizationSideEffectsRef.current) return
        const limitMessage = nextLimits
          ? errorCode === "glasses_draft_limit_reached"
            ? createDraftLimitMessage(nextLimits)
            : createDigitizationLimitMessage(nextLimits)
          : genericErrorMessage

        setStartFeedbackMessage(limitMessage)
        showAlert({
          error: true,
          text: limitMessage,
        })
        return
      }

      if (details?.draftId) {
        navigate(`/glasses/add?draftId=${details.draftId}`, { replace: true })
        try {
          await reloadCurrentEntity({ draftId: details.draftId })
          clearProductCache()
        } catch {
          setEditor((current) => ({
            ...current,
            draftId: details.draftId ?? current.draftId,
            digitization: {
              ...current.digitization,
              lastError: message,
              status: "failed",
            },
          }))
        }
      } else if (details?.productId) {
        try {
          await reloadCurrentEntity({ productId: details.productId })
          clearProductCache()
        } catch {
          setEditor((current) => ({
            ...current,
            digitization: {
              ...current.digitization,
              lastError: message,
              status: "failed",
            },
            productId: details.productId,
          }))
        }
      } else {
        setEditor((current) => ({
          ...current,
          digitization: {
            ...current.digitization,
            lastError: message,
            status: "failed",
          },
        }))
      }

      setStartFeedbackMessage(message)

      showAlert({
        error: true,
        text: message,
      })
    } finally {
      if (allowStartDigitizationSideEffectsRef.current) setIsStarting(false)
    }
  }

  const saveChanges = async () => {
    if (!editor.productId && !editor.draftId) return

    setIsSaving(true)
    try {
      const previousVisible = persistedVisibleRef.current
      const nextName = createFallbackName(editor.name)
      const externalId = editor.externalId.trim() || null
      const cover = await uploadCoverIfNeeded()
      let effectUrl = editor.effectUrl?.trim() || undefined

      if (renderingDirty && isEditableTryOnReady && editor.effectUrl) {
        const {
          data: { authorizeCDN },
        } = await tintVTOService.authorizeCDN()
        const zipBlob = await createDigitizedGlassesZipEffect({
          effectUrl: editor.effectUrl,
          renderingOptions: runtimeEffect.renderingOptions,
        })

        effectUrl = await tintVTOService.uploadCDN({
          body: zipBlob,
          jwt: authorizeCDN,
          name: `effect/products/${editor.merchantId}/glasses/${
            editor.productId ?? editor.draftId ?? "draft"
          }/manual_${Date.now()}.zip`,
          type: "application/zip",
        })
      }

      const savePayload = {
        cover,
        ...(effectUrl ? { effectUrl } : {}),
        externalId,
        name: nextName,
        url: editor.url || null,
        visible: editor.visible,
      }

      if (editor.productId) {
        const sentCoverFile = editor.coverFile
        const saved = await tintVTOService.saveGlassesEditor(editor.productId, savePayload)
        try {
          await syncTiendanubeStoreVisibility({
            merchantId: saved.merchantId,
            nextVisible: saved.visible,
            previousVisible,
            productId: editor.productId,
          })
        } catch {
          clearProductCache()
          showAlert({
            error: true,
            text: "Product saved, but store visibility sync failed.",
          })
          navigate("/glasses")
          return
        }
        void loadLimits(saved.merchantId)
        capturePlatformEvent("product_setup_completed", {
          category: "GLASSES",
          digitization_status: editor.digitization.status,
          is_new_product: false,
          product_id: editor.productId,
          visible: saved.visible,
        })
        clearProductCache()
        // Stay on the page: re-baseline from the canonical post-save state instead of
        // navigating away. Edits typed while the save was in flight stay unsaved.
        persistedVisibleRef.current = saved.visible
        setSavedEditorFields(toSavedEditorFields(saved))
        setEditor((current) => ({
          ...current,
          ...(current.coverFile === sentCoverFile
            ? { coverFile: null, coverPreviewUrl: saved.cover }
            : {}),
          digitization: saved.digitization,
          effectUrl: saved.effectUrl ?? null,
        }))
        const nextEffectUrl = saved.effectUrl ?? null
        if (currentEffectUrlRef.current !== nextEffectUrl) {
          // A re-zipped effect was uploaded: let the viewer re-apply it and rehydrate
          // the sliders from the saved transforms.
          currentEffectUrlRef.current = nextEffectUrl
          setRuntimeEffect(createInitialRuntimeEffectState(nextEffectUrl ? "loading" : "idle"))
          setRenderingDirty(false)
        }
        showAlert({
          error: false,
          text: t("productPage.saveSuccess", {
            name: saved.name,
            action: t("productPage.updated"),
          }),
        })
        return
      } else if (editor.draftId && isDraftReadyToFinalize) {
        const saved = await tintVTOService.finalizeGlassesDraft(editor.draftId, {
          ...savePayload,
          ...(editor.initialSkus ? { initialSkus: editor.initialSkus } : {}),
        })
        void loadLimits(saved.merchantId)
        if (saved.productId)
          try {
            await syncTiendanubeStoreVisibility({
              merchantId: saved.merchantId,
              nextVisible: saved.visible,
              previousVisible: false,
              productId: saved.productId,
            })
          } catch {
            clearProductCache()
            showAlert({
              error: true,
              text: "Product saved, but store visibility sync failed.",
            })
            navigate("/glasses")
            return
          }

        if (saved.productId)
          capturePlatformEvent("product_setup_completed", {
            category: "GLASSES",
            digitization_status: editor.digitization.status,
            is_new_product: true,
            product_id: saved.productId,
            visible: saved.visible,
          })
        clearProductCache()
        navigate("/glasses")
        return
      } else if (editor.draftId) {
        const saved = await tintVTOService.saveGlassesDraft(editor.draftId, {
          cover,
          externalId,
          ...(editor.initialSkus ? { initialSkus: editor.initialSkus } : {}),
          name: nextName,
          url: editor.url || null,
        })
        void loadLimits(saved.merchantId)
        clearProductCache()
        navigate("/glasses")
        return
      }

      clearProductCache()
    } catch (error) {
      showAlert({
        error: true,
        text: getErrorMessage(error, genericErrorMessage),
      })
    } finally {
      setIsSaving(false)
    }
  }

  const deleteDraft = async () => {
    if (!editor.draftId || editor.productId) return

    setIsCancellingDraft(true)
    try {
      await tintVTOService.deleteGlassesDraft(editor.draftId)
      removeDraftFromCachedCollections(queryClient, editor.draftId)
      clearProductCache()
      showAlert({
        error: false,
        text: "Draft cancelled",
      })
      navigate("/glasses")
    } catch (error) {
      showAlert({
        error: true,
        text: getErrorMessage(error, genericErrorMessage),
      })
    } finally {
      setIsCancellingDraft(false)
    }
  }

  const deleteProduct = async () => {
    if (!editor.productId) return

    setIsDeleting(true)
    try {
      if (userInfo.integrationType === IntegrationType.TIENDANUBE) {
        const product = await tintVTOService.productByIdV2(editor.productId)
        const skus = product.colors.map((color) => color.sku).filter(Boolean)
        if (skus.length > 0) await axiosService.updateMerchantSkus(editor.merchantId, [], skus)
      }

      const response = await tintVTOService.deleteProduct(editor.productId)
      if (response.errors) throw new Error(genericErrorMessage)

      removeProductFromCachedCollections(queryClient, editor.productId)
      clearProductCache()
      navigate("/glasses")
    } catch {
      showAlert({
        error: true,
        text: genericErrorMessage,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const isDigitizationActive =
    isStarting ||
    editor.digitization.status === "queued" ||
    editor.digitization.status === "processing"
  const canSave =
    !isSaving && hasRequiredFields(editor) && (Boolean(editor.productId) || Boolean(editor.draftId))

  const isExistingProduct = Boolean(editor.productId)
  const hasUnsavedChanges =
    isExistingProduct &&
    !isLoading &&
    savedEditorFields !== null &&
    (JSON.stringify(toEditorFields(editor)) !== JSON.stringify(savedEditorFields) ||
      editor.coverFile !== null ||
      renderingDirty)

  const isSaveDisabled =
    isSaving ||
    isDigitizationActive ||
    (isDraftEntity && !isDraftReadyToFinalize) ||
    (isExistingProduct && !hasUnsavedChanges)

  const saveActionLabel = editor.productId
    ? t("productPage.saveChanges")
    : isDraftReadyToFinalize
    ? "Save product"
    : "Save"
  const glassesTransformMode: "raw" | "controlled" = renderingDirty ? "controlled" : "raw"

  return {
    canPublishProduct: canPublishProduct(editor),
    canSave,
    canStartDigitization,
    deleteDraft,
    deleteProduct,
    closeEditor: () => {
      allowStartDigitizationSideEffectsRef.current = false
      navigate("/glasses")
    },
    discardChanges: () => {
      if (!isExistingProduct || !savedEditorFields) {
        allowStartDigitizationSideEffectsRef.current = false
        navigate("/glasses")
        return
      }
      if (!hasUnsavedChanges) return
      setEditor((current) => ({
        ...current,
        coverFile: null,
        coverPreviewUrl: savedEditorFields.coverPreviewUrl,
        externalId: savedEditorFields.externalId,
        name: savedEditorFields.name,
        url: savedEditorFields.url,
        visible: savedEditorFields.visible,
      }))
      if (renderingDirty) {
        setRenderingDirty(false)
        setRuntimeEffect(createInitialRuntimeEffectState(editor.effectUrl ? "loading" : "idle"))
        // The viewer applies transforms imperatively; remount it so the saved effect
        // transforms are re-applied and re-resolved.
        setPreviewResetToken((token) => token + 1)
      }
    },
    editor,
    hasUnsavedChanges,
    isExistingProduct,
    previewResetToken,
    isCancellingDraft,
    isDraftEntity,
    isDeleting,
    isEditableTryOnReady,
    isLoading,
    isParsingTryOn,
    isReady,
    isSaveDisabled,
    isSaving,
    isStarting,
    previewImage,
    previewProduct,
    saveChanges,
    saveActionLabel,
    setCover: (file: File) =>
      setEditor((current) => ({
        ...current,
        coverFile: file,
        coverPreviewUrl: URL.createObjectURL(file),
      })),
    setField: (field: "externalId" | "name" | "url", value: string) =>
      setEditor((current) => ({ ...current, [field]: value })),
    hydrateRenderingOptionsFromViewer: (nextValue: RuntimeGlassesControls | null) => {
      if (!editor.effectUrl) {
        setRuntimeEffect(createInitialRuntimeEffectState())
        return
      }

      setRuntimeEffect((current) => {
        if (!nextValue)
          return {
            ...current,
            hydrationStatus: "fallback",
          }

        if (renderingDirty)
          return {
            ...current,
            hydrationStatus: "ready",
          }

        return {
          hydrationStatus: "ready",
          renderingOptions: applyRuntimeControls(current.renderingOptions, nextValue),
        }
      })
    },
    glassesTransformMode,
    setPreviewImage,
    setRenderingOptions: (nextValue: GlassesRenderingOptions) => {
      setRuntimeEffect({
        hydrationStatus: "ready",
        renderingOptions: cloneRenderingOptions(nextValue),
      })
      setRenderingDirty(true)
    },
    removeSourceImage: (imageId: string) =>
      setEditor((current) => ({
        ...current,
        sourceImages: current.sourceImages.filter((image) => image.id !== imageId),
      })),
    setSourceImages: (files: File[]) =>
      setEditor((current) => ({
        ...current,
        sourceImages: files.slice(0, 3).map(toLocalImage),
      })),
    addSourceImages: (files: File[]) =>
      setEditor((current) => ({
        ...current,
        sourceImages: [...current.sourceImages, ...files.map(toLocalImage)].slice(0, 3),
      })),
    setVisible: (visible: boolean) =>
      setEditor((current) => ({
        ...current,
        visible: canPublishProduct(current) ? visible : false,
      })),
    startRestrictionMessage,
    startFeedbackMessage,
    startDigitization,
    tryOnHydrationStatus: runtimeEffect.hydrationStatus,
  }
}
